import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { AppUser, fireauth } from "../auth/AuthSlice"
import { Page, Pageable, defaultPageContent } from "../admin/AdminSlice"
import { RootState } from "../../app/store"
import axios from "axios"
import { API_BASE } from "../../app/const"

export interface AdminUser {
  role: string
  userId: string
  userEmail: string
  paidTier: number
  paidTierCount: number
  paidTierExpiry: number
}

export interface AdminUserState {
  status: 'idle' | 'loading' | 'failed'
  user: AdminUser
  users: Page<AdminUser>
  allRoles: string[]
}

const initialState: AdminUserState = {
  status: 'idle',
  user: {
    role: '',
    userId: '',
    userEmail: '',
    paidTier: 0,
    paidTierCount: 0,
    paidTierExpiry: 0,
  },
  users: defaultPageContent,
  allRoles: []
}

export const fetchAllUserRolesAsync = createAsyncThunk(
  'account/fetchAllUserRolesAsync', async ({ mode }: { mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/user/allRoles`, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const fetchAllAdminUsersAsync = createAsyncThunk(
  'account/fetchAllUsersAsync', async ({ mode, page = { page: 0, size: 10, sort: 'userEmail,asc' } }: { mode: string, page?: Pageable }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/user/all`, {
        params: page,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const searchAdminUsersAsync = createAsyncThunk(
  'account/searchAdminUsersAsync', async ({ query, page = { page: 0, size: 10, sort: 'userEmail,asc' } }: { query: string, page?: Pageable }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.get(`${API_BASE}apps/user/search/${query}`, {
        params: page,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const saveAdminUserAsync = createAsyncThunk(
  'admin/saveUserAsync', async ({ adminUser, mode }: { adminUser: AdminUser, mode: string }, { getState }) => {
    const state = getState() as RootState
    if (state) {
      const response = await axios.post(`${API_BASE}apps/user/role`, adminUser, {
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "Authorization": `Bearer ${await fireauth.currentUser?.getIdToken()}`
        }
      })
      return response.data
    }
  }
)

export const adminUserSlice = createSlice({
  name: 'admin-user',
  initialState,
  reducers: {
    resetAdminUser: (state) => {
      state.status = 'idle'
      state.user = initialState.user
      state.users = initialState.users
    },
    pickAdminUser: (state, action) => {
      state.status = 'idle'
      state.user = action.payload
    }
  },
  extraReducers: (builder) => {
    builder.
      addCase(fetchAllAdminUsersAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllAdminUsersAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.user = initialState.user
        state.users = action.payload
      }).addCase(fetchAllAdminUsersAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.user = initialState.user
        state.users = initialState.users
      }).addCase(searchAdminUsersAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(searchAdminUsersAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.users = action.payload
      }).addCase(searchAdminUsersAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.users = initialState.users
      }).addCase(saveAdminUserAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(saveAdminUserAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.user = action.payload
      }).addCase(saveAdminUserAsync.rejected, (state, action) => {
        state.status = 'failed'
        state.user = initialState.user
      }).addCase(fetchAllUserRolesAsync.pending, (state) => {
        state.status = 'loading'
      }).addCase(fetchAllUserRolesAsync.fulfilled, (state, action) => {
        state.status = 'idle'
        state.allRoles = action.payload
      }).addCase(fetchAllUserRolesAsync.rejected, (state, action) => {
        state.status = 'failed'
      })
  }
})

export const { resetAdminUser, pickAdminUser } = adminUserSlice.actions
export const selectAllAdminUsers = (state: RootState) => state.adminUser.users
export const selectAdminUser = (state: RootState) => state.adminUser.user
export const selectAllUserRoles = (state: RootState) => state.adminUser.allRoles
export const selectAdminUserStatus = (state: RootState) => state.adminUser.status
export default adminUserSlice.reducer